<template>
  <main class="delete-account-page">
    <section class="delete-account-shell" aria-labelledby="delete-account-title">
      <header class="delete-account-brand">
        <span class="brand-mark" aria-hidden="true">
          <img src="img/logo.svg"/>
        </span>
        <div>
          <p class="brand-name">Show Up &amp; Play</p>
          <p class="brand-caption">Account center</p>
        </div>
      </header>

      <div v-if="step !== 'success'" class="step-progress" aria-label="Progress">
        <div
          v-for="(item, index) in steps"
          :key="item.key"
          class="step-progress-item"
          :class="{
            active: currentStepIndex === index,
            complete: currentStepIndex > index,
          }"
        >
          <span class="step-number">
            <i
              v-if="currentStepIndex > index"
              class="tim-icons icon-check-2"
              aria-hidden="true"
            ></i>
            <span v-else>{{ index + 1 }}</span>
          </span>
          <span>{{ item.label }}</span>
        </div>
      </div>

      <div class="delete-account-card">
        <div v-if="step === 'phone'">
          <p class="eyebrow">Delete your account</p>
          <h1 id="delete-account-title">First, verify it’s you</h1>
          <p class="intro-copy">
            Enter the phone number connected to your account. We’ll send you a
            one-time verification code.
          </p>

          <form novalidate @submit.prevent="sendOtp">
            <div class="field-group">
              <label for="delete-account-phone">Phone number</label>
              <div class="phone-fields">
                <input
                  id="delete-account-phone"
                  ref="phoneInput"
                  v-model="phoneNumber"
                  type="tel"
                  inputmode="numeric"
                  autocomplete="tel-national"
                  maxlength="15"
                  placeholder="Phone number"
                  @input="sanitizePhoneNumber"
                />
              </div>
              <p class="field-help">
                Use the same number you use to sign in to the app.
              </p>
            </div>

            <div v-if="errorMessage" class="form-alert error" role="alert">
              <i class="tim-icons icon-alert-circle-exc" aria-hidden="true"></i>
              <span>{{ errorMessage }}</span>
            </div>

            <button
              class="primary-action"
              type="submit"
              :disabled="isBusy"
            >
              <i
                v-if="isBusy"
                class="fas fa-spinner fa-spin"
                aria-hidden="true"
              ></i>
              <span>{{ isBusy ? "Sending code…" : "Continue" }}</span>
            </button>
          </form>

          <p class="privacy-note">
            This secure process is used only to confirm account ownership.
          </p>
        </div>

        <div v-else-if="step === 'otp'">
          <button class="back-action" type="button" @click="changePhoneNumber">
            <i class="tim-icons icon-minimal-left" aria-hidden="true"></i>
            Change phone number
          </button>

          <p class="eyebrow">Verification</p>
          <h1 id="delete-account-title">Enter your code</h1>
          <p class="intro-copy">
            We sent a 6-digit code to
            <strong>{{ formattedPhoneNumber }}</strong>.
          </p>

          <form novalidate @submit.prevent="verifyOtp">
            <div class="field-group">
              <label for="delete-account-otp">Verification code</label>
              <input
                id="delete-account-otp"
                ref="otpInput"
                v-model="otp"
                class="otp-input"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxlength="6"
                placeholder="000000"
                @input="sanitizeOtp"
              />
            </div>

            <div v-if="errorMessage" class="form-alert error" role="alert">
              <i class="tim-icons icon-alert-circle-exc" aria-hidden="true"></i>
              <span>{{ errorMessage }}</span>
            </div>

            <div v-if="noticeMessage" class="form-alert success" role="status">
              <i class="tim-icons icon-check-2" aria-hidden="true"></i>
              <span>{{ noticeMessage }}</span>
            </div>

            <button
              class="primary-action"
              type="submit"
              :disabled="isBusy"
            >
              <i
                v-if="isBusy"
                class="fas fa-spinner fa-spin"
                aria-hidden="true"
              ></i>
              <span>{{ isBusy ? "Verifying…" : "Verify code" }}</span>
            </button>
          </form>

          <div class="resend-row">
            <span>Didn’t receive the code?</span>
            <button
              type="button"
              :disabled="isBusy || resendSeconds > 0"
              @click="resendOtp"
            >
              {{
                resendSeconds > 0
                  ? `Resend in ${resendSeconds}s`
                  : "Resend code"
              }}
            </button>
          </div>
        </div>

        <div v-else-if="step === 'confirm'">
          <p class="eyebrow danger">Final step</p>
          <h1 id="delete-account-title">Permanently delete your account?</h1>
          <p class="intro-copy">
            Your phone number has been verified. This action cannot be undone.
          </p>

          <div class="deletion-warning">
            <div class="warning-icon" aria-hidden="true">
              <i class="tim-icons icon-trash-simple"></i>
            </div>
            <div>
              <h2>What happens next</h2>
              <ul>
                <li>Your account and profile will be permanently removed.</li>
                <li>You will lose access to your account in the app.</li>
                <li>This deletion cannot be reversed after confirmation.</li>
              </ul>
            </div>
          </div>

          <label class="confirm-check">
            <input v-model="deletionConfirmed" type="checkbox" />
            <span>
              I understand that deleting my account is permanent and cannot be
              undone.
            </span>
          </label>

          <div v-if="errorMessage" class="form-alert error" role="alert">
            <i class="tim-icons icon-alert-circle-exc" aria-hidden="true"></i>
            <span>{{ errorMessage }}</span>
          </div>

          <button
            class="delete-action"
            type="button"
            :disabled="isBusy || !deletionConfirmed"
            @click="deleteAccount"
          >
            <i
              v-if="isBusy"
              class="fas fa-spinner fa-spin"
              aria-hidden="true"
            ></i>
            <i v-else class="tim-icons icon-trash-simple" aria-hidden="true"></i>
            <span>{{ isBusy ? "Deleting account…" : "Delete my account" }}</span>
          </button>

          <button
            class="cancel-action"
            type="button"
            :disabled="isBusy"
            @click="startOver"
          >
            Cancel and start over
          </button>
        </div>

        <div v-else class="success-state">
          <div class="success-mark" aria-hidden="true">
            <i class="tim-icons icon-check-2"></i>
          </div>
          <p class="eyebrow">Request complete</p>
          <h1 id="delete-account-title">Your account has been deleted</h1>
          <p class="intro-copy">
            Your Show Up &amp; Play account is no longer active. You can safely
            close this page.
          </p>
        </div>
      </div>

      <footer class="delete-account-footer">
        <span>Show Up &amp; Play</span>
        <span aria-hidden="true">•</span>
        <span>Secure account deletion</span>
      </footer>
    </section>
  </main>
</template>

<script>
import {
  deleteAccountSession,
  deleteVerifiedAccount,
  requestDeleteAccountOtp,
  verifyDeleteAccountOtp,
} from "src/services/delete-account-service";

const RESEND_DELAY_SECONDS = 30;

export default {
  data() {
    return {
      step: "phone",
      steps: [
        { key: "phone", label: "Phone" },
        { key: "otp", label: "Verify" },
        { key: "confirm", label: "Delete" },
      ],
      dialCodeDigits: "1",
      phoneNumber: "",
      otp: "",
      deletionConfirmed: false,
      verificationToken: "",
      verifiedUserId: "",
      isBusy: false,
      errorMessage: "",
      noticeMessage: "",
      resendSeconds: 0,
      resendTimer: null,
    };
  },
  computed: {
    currentStepIndex() {
      return this.steps.findIndex((item) => item.key === this.step);
    },
    dialCode() {
      return `+${this.dialCodeDigits}`;
    },
    formattedPhoneNumber() {
      if (!this.phoneNumber) return this.dialCode;

      const visibleDigits = this.phoneNumber.slice(-4);
      const maskedDigits = "•".repeat(
        Math.max(this.phoneNumber.length - visibleDigits.length, 0)
      );

      return `${this.dialCode} ${maskedDigits}${visibleDigits}`;
    },
  },
  mounted() {
    const deletionSession = deleteAccountSession.get();

    if (deletionSession.dialCode) {
      this.dialCodeDigits = deletionSession.dialCode.replace(/\D/g, "") || "1";
    }

    if (deletionSession.phoneNumber) {
      this.phoneNumber = deletionSession.phoneNumber;
    }

    if (deletionSession.token && deletionSession.userId) {
      this.verificationToken = deletionSession.token;
      this.verifiedUserId = deletionSession.userId;
      this.step = "confirm";
    } else {
      this.focusField("phoneInput");
    }
  },
  beforeDestroy() {
    this.stopResendTimer();
  },
  methods: {
    sanitizeDialCode() {
      this.dialCodeDigits = this.dialCodeDigits.replace(/\D/g, "").slice(0, 4);
    },
    sanitizePhoneNumber() {
      this.phoneNumber = this.phoneNumber.replace(/\D/g, "").slice(0, 15);
    },
    sanitizeOtp() {
      this.otp = this.otp.replace(/\D/g, "").slice(0, 6);
    },
    validatePhone() {
      if (!this.dialCodeDigits || !/^\d{1,4}$/.test(this.dialCodeDigits)) {
        return "Enter a valid country calling code.";
      }

      if (!/^\d{6,15}$/.test(this.phoneNumber)) {
        return "Enter a valid phone number using 6 to 15 digits.";
      }

      return "";
    },
    async sendOtp() {
      const validationMessage = this.validatePhone();
      if (validationMessage) {
        this.errorMessage = validationMessage;
        return;
      }

      this.isBusy = true;
      this.errorMessage = "";
      this.noticeMessage = "";
      deleteAccountSession.clearVerification();

      try {
        await requestDeleteAccountOtp(this.dialCode, this.phoneNumber);
        deleteAccountSession.savePhone(this.dialCode, this.phoneNumber);
        this.step = "otp";
        this.otp = "";
        this.startResendTimer();
        this.focusField("otpInput");
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.isBusy = false;
      }
    },
    async resendOtp() {
      if (this.resendSeconds > 0) return;

      this.isBusy = true;
      this.errorMessage = "";
      this.noticeMessage = "";

      try {
        await requestDeleteAccountOtp(this.dialCode, this.phoneNumber);
        this.noticeMessage = "A new verification code has been sent.";
        this.startResendTimer();
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.isBusy = false;
      }
    },
    async verifyOtp() {
      if (!/^\d{6}$/.test(this.otp)) {
        this.errorMessage = "Enter the 6-digit verification code.";
        return;
      }

      this.isBusy = true;
      this.errorMessage = "";
      this.noticeMessage = "";

      try {
        const verification = await verifyDeleteAccountOtp(
          this.phoneNumber,
          this.otp,
          deleteAccountSession.getOrCreateFcmToken()
        );

        deleteAccountSession.saveVerification(
          verification.token,
          verification.userId
        );
        this.verificationToken = verification.token;
        this.verifiedUserId = verification.userId;
        this.stopResendTimer();
        this.deletionConfirmed = false;
        this.step = "confirm";
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.isBusy = false;
      }
    },
    async deleteAccount() {
      if (!this.deletionConfirmed) return;

      const deletionSession = deleteAccountSession.get();
      const token = this.verificationToken || deletionSession.token;
      const userId = this.verifiedUserId || deletionSession.userId;

      if (!token || !userId) {
        this.errorMessage =
          "Your verification session has expired. Please verify your phone number again.";
        deleteAccountSession.clearVerification();
        this.step = "phone";
        return;
      }

      this.isBusy = true;
      this.errorMessage = "";

      try {
        await deleteVerifiedAccount(userId, token);
        deleteAccountSession.clear();
        this.verificationToken = "";
        this.verifiedUserId = "";
        this.step = "success";
      } catch (error) {
        if (error.status === 401 || error.status === 403) {
          deleteAccountSession.clearVerification();
          this.verificationToken = "";
          this.verifiedUserId = "";
          this.step = "phone";
          this.deletionConfirmed = false;
          this.errorMessage =
            "Your verification session has expired. Please verify your phone number again.";
        } else {
          this.errorMessage = error.message;
        }
      } finally {
        this.isBusy = false;
      }
    },
    changePhoneNumber() {
      this.stopResendTimer();
      deleteAccountSession.clearVerification();
      this.verificationToken = "";
      this.verifiedUserId = "";
      this.step = "phone";
      this.otp = "";
      this.errorMessage = "";
      this.noticeMessage = "";
      this.focusField("phoneInput");
    },
    startOver() {
      this.stopResendTimer();
      deleteAccountSession.clear();
      this.step = "phone";
      this.phoneNumber = "";
      this.otp = "";
      this.deletionConfirmed = false;
      this.verificationToken = "";
      this.verifiedUserId = "";
      this.errorMessage = "";
      this.noticeMessage = "";
      this.focusField("phoneInput");
    },
    startResendTimer() {
      this.stopResendTimer();
      this.resendSeconds = RESEND_DELAY_SECONDS;
      this.resendTimer = window.setInterval(() => {
        this.resendSeconds -= 1;
        if (this.resendSeconds <= 0) this.stopResendTimer();
      }, 1000);
    },
    stopResendTimer() {
      if (this.resendTimer) {
        window.clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
      this.resendSeconds = 0;
    },
    focusField(referenceName) {
      this.$nextTick(() => {
        const field = this.$refs[referenceName];
        if (field && field.focus) field.focus();
      });
    },
  },
};
</script>

<style scoped>
.delete-account-page {
  align-items: center;
  background:
    radial-gradient(circle at 12% 8%, rgba(225, 78, 202, 0.14), transparent 34%),
    radial-gradient(circle at 88% 84%, rgba(0, 242, 195, 0.08), transparent 32%),
    #07080d;
  color: #f6f7fb;
  display: flex;
  min-height: 100vh;
  padding: 42px 20px;
}

.delete-account-shell {
  margin: 0 auto;
  max-width: 620px;
  width: 100%;
}

.delete-account-brand {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 30px;
}

.brand-mark {
  align-items: center;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(225, 78, 202, 0.25);
  display: inline-flex;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.brand-mark i {
  color: #ffffff;
  font-size: 20px;
}

.brand-name,
.brand-caption {
  margin: 0;
}

.brand-name {
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.brand-caption {
  color: #8992a9;
  font-size: 12px;
  margin-top: 2px;
}

.step-progress {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, 1fr);
  margin: 0 auto 18px;
  max-width: 460px;
}

.step-progress-item {
  align-items: center;
  color: #707990;
  display: flex;
  font-size: 12px;
  font-weight: 600;
  gap: 8px;
  justify-content: center;
}

.step-progress-item.active {
  color: #ffffff;
}

.step-progress-item.complete {
  color: #a88ccd;
}

.step-number {
  align-items: center;
  border: 1px solid #31364a;
  border-radius: 50%;
  display: inline-flex;
  height: 25px;
  justify-content: center;
  transition: 0.2s ease;
  width: 25px;
}

.step-progress-item.active .step-number {
  background: #FF3B30;
  border-color: #FF3B30;
  box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.13);
  color: #ffffff;
}

.step-progress-item.complete .step-number {
  background: rgba(186, 84, 245, 0.12);
  border-color: #8c52b4;
}

.step-number i {
  font-size: 10px;
}

.delete-account-card {
  background: rgba(18, 20, 29, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 22px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.38);
  padding: 42px;
}

.eyebrow {
  color: #FF3B30;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  margin: 0 0 10px;
  text-transform: uppercase;
}

.eyebrow.danger {
  color: #ff7c8b;
}

h1 {
  color: #ffffff;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1.2;
  margin: 0;
}

.intro-copy {
  color: #aeb5c5;
  font-size: 15px;
  line-height: 1.7;
  margin: 14px 0 30px;
}

.intro-copy strong {
  color: #ffffff;
  font-weight: 600;
}

.field-group {
  margin-bottom: 20px;
}

.field-group label {
  color: #e8eaf0;
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 9px;
}

.phone-fields {
}

.dial-code-field {
  position: relative;
}

.field-prefix {
  color: #c4c9d4;
  left: 16px;
  position: absolute;
  top: 14px;
}

.dial-code-field input {
  padding-left: 31px;
}

input[type="tel"],
input[type="text"] {
  background: #0c0e15;
  border: 1px solid #303547;
  border-radius: 11px;
  color: #ffffff;
  font-size: 15px;
  height: 50px;
  outline: none;
  padding: 0 16px;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  width: 100%;
}

input::placeholder {
  color: #626b80;
}

input:focus {
  border-color: #fb2b1f;
  box-shadow: 0 0 0 3px rgba(186, 84, 245, 0.13);
}

.field-help,
.privacy-note {
  color: #747d92;
  font-size: 12px;
  line-height: 1.5;
  margin: 8px 0 0;
}

.otp-input {
  font-size: 24px !important;
  font-weight: 700;
  letter-spacing: 0.42em;
  text-align: center;
}

.primary-action,
.delete-action,
.cancel-action {
  align-items: center;
  border: 0;
  border-radius: 11px;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 700;
  gap: 9px;
  justify-content: center;
  min-height: 50px;
  padding: 0 18px;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
  width: 100%;
}

.primary-action {
  background: linear-gradient(135deg, #FF3B30, #fb2b1f);
  box-shadow: 0 9px 24px rgba(186, 84, 245, 0.23);
  color: #ffffff;
}

.primary-action:not(:disabled):hover,
.delete-action:not(:disabled):hover {
  box-shadow: 0 12px 30px rgba(186, 84, 245, 0.32);
  transform: translateY(-1px);
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.48;
}

.privacy-note {
  margin-top: 17px;
  text-align: center;
}

.back-action {
  align-items: center;
  background: transparent;
  border: 0;
  color: #9aa3b7;
  cursor: pointer;
  display: inline-flex;
  font-size: 12px;
  gap: 7px;
  margin: -6px 0 24px;
  padding: 4px 0;
}

.back-action:hover {
  color: #ffffff;
}

.form-alert {
  align-items: flex-start;
  border: 1px solid;
  border-radius: 10px;
  display: flex;
  font-size: 13px;
  gap: 9px;
  line-height: 1.5;
  margin-bottom: 18px;
  padding: 12px 14px;
}

.form-alert i {
  margin-top: 3px;
}

.form-alert.error {
  background: rgba(253, 93, 112, 0.08);
  border-color: rgba(253, 93, 112, 0.28);
  color: #ff9eaa;
}

.form-alert.success {
  background: rgba(0, 242, 195, 0.06);
  border-color: rgba(0, 242, 195, 0.24);
  color: #75e7ce;
}

.resend-row {
  align-items: center;
  color: #7f889c;
  display: flex;
  font-size: 12px;
  gap: 5px;
  justify-content: center;
  margin-top: 20px;
}

.resend-row button {
  background: transparent;
  border: 0;
  color: #FF3B30;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 3px;
}

.resend-row button:disabled {
  color: #747d92;
}

.deletion-warning {
  align-items: flex-start;
  background: rgba(253, 93, 112, 0.06);
  border: 1px solid rgba(253, 93, 112, 0.22);
  border-radius: 14px;
  display: flex;
  gap: 14px;
  margin-bottom: 22px;
  padding: 18px;
}

.warning-icon {
  align-items: center;
  background: rgba(253, 93, 112, 0.14);
  border-radius: 10px;
  color: #ff7c8b;
  display: flex;
  flex: 0 0 38px;
  height: 38px;
  justify-content: center;
}

.deletion-warning h2 {
  color: #ffffff;
  font-size: 14px;
  margin: 0 0 9px;
}

.deletion-warning ul {
  color: #aeb5c5;
  font-size: 12px;
  line-height: 1.6;
  margin: 0;
  padding-left: 18px;
}

.confirm-check {
  align-items: flex-start;
  color: #c7ccd7;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  gap: 11px;
  line-height: 1.55;
  margin: 0 0 20px;
}

.confirm-check input {
  accent-color: #FF3B30;
  flex: 0 0 auto;
  height: 17px;
  margin-top: 2px;
  width: 17px;
}

.delete-action {
  background: linear-gradient(135deg, #fd5d70, #d94458);
  box-shadow: 0 9px 24px rgba(253, 93, 112, 0.18);
  color: #ffffff;
}

.cancel-action {
  background: transparent;
  color: #919aad;
  margin-top: 8px;
  min-height: 40px;
}

.cancel-action:not(:disabled):hover {
  color: #ffffff;
}

.success-state {
  padding: 24px 4px;
  text-align: center;
}

.success-mark {
  align-items: center;
  background: rgba(0, 242, 195, 0.1);
  border: 1px solid rgba(0, 242, 195, 0.28);
  border-radius: 50%;
  color: #4ce2c4;
  display: flex;
  height: 72px;
  justify-content: center;
  margin: 0 auto 24px;
  width: 72px;
}

.success-mark i {
  font-size: 24px;
}

.success-state .intro-copy {
  margin-bottom: 0;
}

.delete-account-footer {
  align-items: center;
  color: #5f6778;
  display: flex;
  font-size: 11px;
  gap: 8px;
  justify-content: center;
  margin-top: 22px;
}

@media (max-width: 575px) {
  .delete-account-page {
    align-items: flex-start;
    padding: 24px 14px;
  }

  .delete-account-brand {
    justify-content: flex-start;
    margin: 0 6px 24px;
  }

  .delete-account-card {
    border-radius: 18px;
    padding: 30px 22px;
  }

  h1 {
    font-size: 25px;
  }

  .step-progress-item {
    gap: 5px;
  }

  .step-progress-item > span:last-child {
    display: none;
  }

  .deletion-warning {
    padding: 15px;
  }
}
</style>
